import React, { useEffect } from "react";
import {
  X, HelpCircle, Plus, Home, NotebookPen, ShieldCheck, Settings2,
  Layers, Lock, LineChart, Target, PiggyBank, Wallet,
} from "lucide-react";

// Single in-app "Guide Notes" — plain-language help for non-expert users.
// Pure overlay: does NOT change any page structure or navigation.

function Item({ children }) {
  return <li className="flex gap-2 text-[13px] leading-relaxed text-zinc-600"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-500" /><span className="flex-1">{children}</span></li>;
}

function GuideBlock({ Icon, title, children, defaultOpen = false, testId }) {
  return (
    <details className="rounded-2xl border border-zinc-200 bg-white p-4" open={defaultOpen} data-testid={testId}>
      <summary className="flex cursor-pointer list-none items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <Icon className="h-4 w-4" />
        </span>
        <span className="font-heading text-sm font-bold text-zinc-900">{title}</span>
        <Plus className="ml-auto h-4 w-4 text-zinc-400 transition-transform [details[open]_&]:rotate-45" />
      </summary>
      <div className="mt-3 space-y-2 border-t border-zinc-100 pt-3">{children}</div>
    </details>
  );
}

export function GuideSheet({ open, onClose }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-center" data-testid="guide-sheet">
      <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm" onClick={onClose} data-testid="guide-backdrop" />
      <div className="relative flex h-full w-full max-w-md lg:max-w-2xl lg:mx-auto lg:my-8 lg:h-auto lg:max-h-[85vh] lg:rounded-3xl lg:shadow-2xl flex-col bg-[#f8f8f7]">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-zinc-200 bg-white px-5 py-4">
          <HelpCircle className="h-5 w-5 text-emerald-600" />
          <h2 className="font-heading text-lg font-extrabold text-zinc-900">Panduan CeKKeU</h2>
          <button onClick={onClose} data-testid="guide-close" className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100" aria-label="Tutup panduan">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="no-scrollbar flex-1 space-y-3 overflow-y-auto p-5 pb-16">
          <p className="text-[13px] leading-relaxed text-zinc-500">
            Panduan singkat cara memakai semua fitur. Ketuk tiap bagian untuk membukanya. Semua angka keuangan Anda
            tersimpan hanya di perangkat ini dan tidak dikirim ke mana pun.
          </p>
          <GuideBlock Icon={Layers} title="Hal paling penting: 2 bagian aplikasi" defaultOpen testId="guide-two-worlds">
            <p className="text-[13px] leading-relaxed text-zinc-600">
              CeKKeU punya <b>dua bagian yang terpisah</b>. Wajar kalau angkanya berbeda — memang beda sumber:
            </p>
            <ul className="space-y-1.5">
              <Item><b>1. Tes Ketahanan</b> — sekali isi ±2 menit. Menjawab: “Kalau penghasilan terganggu, keluarga bisa bertahan berapa lama?”. Hasilnya <b>Skor Ketahanan</b> (di tab Ketahanan).</Item>
              <Item><b>2. Pencatatan Harian</b> — Anda catat pemasukan/pengeluaran tiap hari. Menjawab: “Bulan ini uang saya sehat atau boros?”. Hasilnya <b>Skor Kesehatan Harian</b> & analisis (di tab Catatan → Analisis).</Item>
            </ul>
            <p className="rounded-xl bg-amber-50 p-2.5 text-[12px] leading-relaxed text-amber-700">
              Catatan: isian di <b>Pengaturan Keuangan</b> memengaruhi bagian <b>Pencatatan Harian</b> — <b>bukan</b> Skor Ketahanan.
            </p>
          </GuideBlock>

          <GuideBlock Icon={Plus} title="Catat Transaksi (tombol + di tengah)" testId="guide-catat">
            <ul className="space-y-1.5">
              <Item>Pakai untuk mencatat <b>pemasukan</b> (gaji, bonus) atau <b>pengeluaran</b> (makan, transport, dll) setiap hari.</Item>
              <Item>Bisa pilih tanggal (mundur maksimal 31 hari) kalau lupa mencatat kemarin.</Item>
              <Item>Semakin rutin mencatat, semakin akurat skor & proyeksi Anda.</Item>
            </ul>
          </GuideBlock>

          <GuideBlock Icon={Home} title="Beranda" testId="guide-beranda">
            <ul className="space-y-1.5">
              <Item>Ringkasan cepat kondisi bulan ini: skor, sisa/deficit perkiraan, dan pengingat mencatat.</Item>
              <Item>Titik awal harian Anda — lihat sekilas lalu lanjut mencatat.</Item>
            </ul>
          </GuideBlock>

          <GuideBlock Icon={NotebookPen} title="Catatan → Analisis (inti aplikasi)" testId="guide-analisis">
            <p className="text-[13px] leading-relaxed text-zinc-600">Di tab <b>Catatan</b> ada daftar transaksi dan sub-tab <b>Analisis</b>. Isi Analisis:</p>
            <ul className="space-y-1.5">
              <Item><b>Skor Kesehatan Harian (0–100)</b> — makin tinggi makin sehat. Terdiri dari 3 bagian: Arus Kas (45%), Kecepatan Belanja (30%), Dana Darurat (25%). Ketuk “Mengapa skor saya?” untuk penjelasan.</Item>
              <Item><b>Kondisi Saat Ini</b> — angka <b>nyata</b> yang sudah tercatat bulan ini (pemasukan, pengeluaran, surplus/defisit).</Item>
              <Item><b>Proyeksi Akhir Bulan</b> — <b>perkiraan</b> (bukan fakta) sisa atau defisit jika pola belanja diteruskan.</Item>
              <Item><b>Anggaran Aman / hari</b> — kira-kira boleh belanja berapa per hari agar tidak jebol. Tidak akan pernah minus.</Item>
              <Item><b>Dana Darurat menutup ~X bulan</b> — tabungan darurat Anda cukup untuk berapa bulan pengeluaran wajib.</Item>
              <Item><b>Target Hemat</b> — batas belanja per kategori. Muncul “tercapai / lewat / sisa” otomatis dari transaksi.</Item>
              <Item><b>Ringkasan Bulanan</b> — rekap 1 bulan penuh beserta saran yang menyesuaikan kondisi Anda.</Item>
            </ul>
          </GuideBlock>

          <GuideBlock Icon={ShieldCheck} title="Ketahanan (tes & simulasi)" testId="guide-ketahanan">
            <ul className="space-y-1.5">
              <Item><b>Skor Ketahanan</b> dari tes 4-langkah. Ketuk “Apa arti skor Anda?” untuk penjelasan tiap komponen (dana darurat, beban cicilan, dll), dan “Bagaimana skor dihitung?” untuk rinciannya.</Item>
              <Item><b>Simulasi</b> — coba skenario “bagaimana kalau” (mis. gaji berhenti). Fitur premium via langganan bulanan (atau pakai kode voucher promo).</Item>
            </ul>
          </GuideBlock>

          <GuideBlock Icon={Settings2} title="Pengaturan Keuangan — apa efek tiap isian?" testId="guide-setup">
            <p className="text-[13px] leading-relaxed text-zinc-600">Isian di sini menyempurnakan analisis harian Anda:</p>
            <ul className="space-y-1.5">
              <Item><b>Sumber Penghasilan</b> → dasar hitung proyeksi & “anggaran aman/hari”. Kalau kosong, sistem memperkirakannya dari transaksi (kurang akurat).</Item>
              <Item><b>Pengeluaran Rutin & Cicilan</b> → dihitung sebagai “pengeluaran wajib” untuk proyeksi dan ukuran ketahanan dana darurat.</Item>
              <Item><b>Dana Darurat</b> → dipakai kartu “Dana darurat menutup ~X bulan”. Kalau kosong tampil “Belum tersedia”.</Item>
              <Item><b>Target Hemat (per kategori)</b> → batas belanja bulanan yang Anda lihat progresnya di Analisis & Beranda.</Item>
            </ul>
            <p className="text-[12px] leading-relaxed text-zinc-500">Semua opsional. Isi bertahap sesuai kebutuhan.</p>
          </GuideBlock>

          <GuideBlock Icon={LineChart} title="Istilah singkat" testId="guide-glossary">
            <ul className="space-y-1.5">
              <Item><b>Arus kas</b>: selisih pemasukan dan pengeluaran. Positif = surplus, negatif = defisit.</Item>
              <Item><b>Defisit</b>: pengeluaran lebih besar dari pemasukan. Ditutup dari saldo/tabungan/dana darurat.</Item>
              <Item><b>Dana darurat “X bulan”</b>: tabungan darurat ÷ pengeluaran wajib per bulan.</Item>
              <Item><b>Kritis / Waspada / Sehat</b>: kategori skor. 0–49 Kritis · 50–71 Waspada · 72–100 Sehat.</Item>
            </ul>
          </GuideBlock>

          <GuideBlock Icon={Lock} title="Privasi" testId="guide-privacy">
            <ul className="space-y-1.5">
              <Item>Semua nominal (gaji, pengeluaran, dana darurat) disimpan <b>hanya di perangkat Anda</b>.</Item>
              <Item>Nominal tidak pernah dikirim ke server atau admin CeKKeU.</Item>
            </ul>
          </GuideBlock>
        </div>
      </div>
    </div>
  );
}
